/**
 * ArResizeHandle
 *
 * Drag handle painted at the right edge of an ArTable header cell; consumed by
 * ArTableHeader, one per resizable column. Dragging reports the clamped width
 * through `onResize`, a double-click resets the column through `onReset`.
 * The component holds no state: the drag runs on pointer capture with the
 * origin in refs, and the hover, focus and drag highlight is the
 * `.column-resize-handle` CSS in `index.css` swapping the two custom properties
 * below. A `useState` hover flag here would re-render the handle on every
 * pointer entry, and the colour never needs to reach the React tree.
 */

import { FC, memo, useRef, CSSProperties } from 'react';
import { theme } from 'antd';
import type { TypeId } from '#types/System.Artifacts.types';

type ArResizeHandleProps = {
  typeId: TypeId;
  minWidth: number;
  maxWidth: number;
  onResize: (typeId: TypeId, width: number) => void;
  onReset: (typeId: TypeId) => void;
};

type ResizeHandleStyle = CSSProperties & {
  '--column-resize-color': string;
  '--column-resize-color-active': string;
};

type DragOrigin = {
  clientX: number;
  width: number;
};

const ArResizeHandle: FC<ArResizeHandleProps> = ({
  typeId,
  minWidth,
  maxWidth,
  onResize,
  onReset,
}) => {
  const { token } = theme.useToken();
  const dragOriginRef = useRef<DragOrigin | null>(null);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragOriginRef.current = {
      clientX: e.clientX,
      width: e.currentTarget.parentElement?.offsetWidth ?? 100,
    };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const origin = dragOriginRef.current;
    if (!origin) return;
    const delta = e.clientX - origin.clientX;
    const newWidth = Math.max(minWidth, Math.min(origin.width + delta, maxWidth));
    onResize(typeId, newWidth);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    dragOriginRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onReset(typeId);
  };

  const handleStyle: ResizeHandleStyle = {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: 5,
    boxSizing: 'content-box',
    backgroundColor: 'transparent',
    zIndex: 30,
    userSelect: 'none',
    touchAction: 'none',
    '--column-resize-color': token.colorTextTertiary,
    '--column-resize-color-active': token.colorPrimary,
  };

  return (
    <div
      className="column-resize-handle"
      aria-label={`resize-handle-${typeId}`}
      style={handleStyle}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onDoubleClick={handleDoubleClick}
    />
  );
};

export default memo(ArResizeHandle);
